import { z } from 'zod';

import { backupResultSchema } from '../backup/snapshot';
import {
  isoDateTimeSchema,
  nonNegativeIntSchema,
  sha256HexSchema,
  uuidSchema,
} from '../common/primitives';

/**
 * Operationen — jede Zustandsänderung, die der Master an einem Node auslöst.
 *
 * ## Zwei Verben, nicht drei
 *
 * Auf der Leitung gibt es genau `site.apply` und `site.remove`. `apply` ist
 * **konvergierend**: "stelle diesen Zustand her", nicht "erzeuge" oder
 * "aktualisiere" oder "starte". Die Alternative würde den Master zwingen, auf
 * Basis eines bis zu zehn Sekunden alten und womöglich falschen Bildes vom Node
 * zu entscheiden, welches der drei Verben gerade gilt. Ein `create` an einen
 * Node, der den Stack schon hat, müsste dann entweder scheitern oder still
 * umgedeutet werden — beides schlechter als ein Verb, dessen Vertrag keine
 * Vorbedingung kennt. Genau das macht die Wiederholung aus ADR 0004 kostenlos.
 *
 * "Stoppen" ist deshalb kein eigenes Verb, sondern ein `apply` mit
 * `runState: 'stopped'` in der Spezifikation.
 *
 * ## Warum es seit M6a doch vier sind
 *
 * `site.backup` und `site.restore` kommen dazu, und die Begründung oben bleibt
 * dabei unangetastet — sie gilt für konvergierende Verben, und diese beiden
 * sind keine (ADR 0012):
 *
 *   - Eine **Sicherung stellt nichts her**. Sie hält fest. Es gibt keinen
 *     Zustand am Node, den `apply` erreichen könnte und der bedeutete "diese
 *     Site ist gesichert" — der Zustand entsteht anderswo, auf der Storage Box,
 *     und er ist mit jedem Tag ein anderer.
 *   - Ein **Rückweg ersetzt einen Zustand, den der Master gar nicht
 *     beschreibt**. Die Dateien eines Kunden stehen in keiner Spezifikation;
 *     der Master weiß nicht einmal, wie viele es sind. Ein `apply` kann sie
 *     deshalb nicht herstellen, und ein Verb, das sie herstellt, kann nicht
 *     konvergierend sein: Zweimal denselben Rückweg zu gehen ist nicht dasselbe
 *     wie einmal.
 *
 * Was dagegen unverändert gilt: Beide tragen eine `operationId`, und der Agent
 * führt eine ihm bekannte Operation nie zweimal aus. Die Wiederholung aus
 * ADR 0004 bleibt damit auch hier folgenlos.
 */

export const OPERATION_TYPES = {
  /** Stelle den in der Spezifikation beschriebenen Zustand her. */
  apply: 'site.apply',
  /** Entferne alles, was zu dieser Site gehört, rückstandsfrei. */
  remove: 'site.remove',
  /** Halte den jetzigen Stand von Dateien und Datenbanken fest. */
  backup: 'site.backup',
  /** Ersetze den jetzigen Stand durch eine Momentaufnahme. */
  restore: 'site.restore',
} as const;

export type OperationType = (typeof OPERATION_TYPES)[keyof typeof OPERATION_TYPES];

export const operationTypeSchema = z.enum([
  OPERATION_TYPES.apply,
  OPERATION_TYPES.remove,
  OPERATION_TYPES.backup,
  OPERATION_TYPES.restore,
]);

/**
 * Ändert dieses Verb den Soll-Zustand des Stacks?
 *
 * Der Reconciler wertet das aus: Er darf eine Site nicht deshalb für
 * abweichend halten, weil gerade gesichert wird — und er darf einer laufenden
 * Sicherung nicht dazwischenfahren.
 */
export function isConvergingOperation(type: OperationType): boolean {
  return type === OPERATION_TYPES.apply || type === OPERATION_TYPES.remove;
}

/**
 * Lebenslauf einer Operation.
 *
 * Bewusst **kein** eigener `running`-Status: Er würde einen Schreibvorgang je
 * Heartbeat kosten für eine Information, die ohnehin in `activeOperationIds`
 * steht.
 */
export const OPERATION_STATUS = {
  /** Angelegt und noch nicht an den Node übergeben. */
  queued: 'queued',
  /** Der Node hat den Auftrag angenommen (202) und arbeitet ihn ab. */
  dispatched: 'dispatched',
  succeeded: 'succeeded',
  failed: 'failed',
  /** Das Soll hat sich geändert, während die Operation lief. */
  cancelled: 'cancelled',
} as const;

export type OperationStatus = (typeof OPERATION_STATUS)[keyof typeof OPERATION_STATUS];

export const operationStatusSchema = z.enum([
  OPERATION_STATUS.queued,
  OPERATION_STATUS.dispatched,
  OPERATION_STATUS.succeeded,
  OPERATION_STATUS.failed,
  OPERATION_STATUS.cancelled,
]);

/** Endzustände — von hier führt kein Weg zurück. */
export function isTerminalOperationStatus(status: OperationStatus): boolean {
  return (
    status === OPERATION_STATUS.succeeded ||
    status === OPERATION_STATUS.failed ||
    status === OPERATION_STATUS.cancelled
  );
}

// -- Rückmeldung des Agents --------------------------------------------------

/**
 * Ist ein Fehlschlag eine Wiederholung wert?
 *
 * Der Unterschied ist teuer, wenn man ihn nicht macht: Eine strukturell falsche
 * Spezifikation (unbekanntes Image, verbotener Pfad) wird sonst fünfmal an
 * denselben Node geschickt, mit wachsendem Abstand, und erzeugt fünfmal
 * dieselbe Fehlermeldung — statt sofort sichtbar zu scheitern.
 */
export const OPERATION_FAILURE_KIND = {
  /** Vorübergehend: Netz, Registry, Plattenplatz. Erneuter Versuch sinnvoll. */
  retryable: 'retryable',
  /** Dauerhaft: Die Spezifikation ist so, wie sie ist, nicht ausführbar. */
  permanent: 'permanent',
} as const;

export type OperationFailureKind =
  (typeof OPERATION_FAILURE_KIND)[keyof typeof OPERATION_FAILURE_KIND];

export const operationFailureKindSchema = z.enum([
  OPERATION_FAILURE_KIND.retryable,
  OPERATION_FAILURE_KIND.permanent,
]);

/**
 * Bezeichner für Schritte und Fehlercodes.
 *
 * Nicht `slugSchema`: Diese Werte landen nie in einem Dateipfad oder DNS-Namen,
 * dürfen dafür aber länger sein.
 */
const stepNameSchema = z
  .string()
  .max(64)
  .regex(/^[a-z\d]+(?:-[a-z\d]+)*$/, {
    message: 'Nur Kleinbuchstaben, Ziffern und einfache Bindestriche erlaubt.',
  });

/**
 * Ein Arbeitsschritt des Agents.
 *
 * Der eigentliche Wert liegt in `durationMs`: "Image-Pull lief 240 Sekunden und
 * brach dann ab" ist der Satz, der eine Stunde Fehlersuche spart. Aus dem
 * Inventar allein wäre er nie zu gewinnen.
 */
export const operationStepSchema = z.object({
  /** Technischer Bezeichner, z. B. `image-pull`, `container-create`, `route-verify`. */
  name: stepNameSchema,
  status: z.enum(['succeeded', 'failed', 'skipped']),
  durationMs: nonNegativeIntSchema,
  /** Menschenlesbare Kurzfassung auf Deutsch; bei Fehlschlag die Ursache. */
  message: z.string().max(2000),
});

export type OperationStep = z.infer<typeof operationStepSchema>;

/** Die strukturierte Fehlerursache, wie der Agent sie kennt. */
export const operationErrorSchema = z.object({
  kind: operationFailureKindSchema,
  /** Kurzer, stabiler Bezeichner, z. B. `image-pull-failed`. */
  code: stepNameSchema,
  message: z.string().max(2000),
  /**
   * Was der Betreiber tun soll. Pflicht, nicht optional — eine Fehlermeldung
   * ohne Handlungsanweisung verlagert die Arbeit nur auf jemanden, der weniger
   * Kontext hat.
   */
  remediation: z.string().max(2000),
});

export type OperationError = z.infer<typeof operationErrorSchema>;

/**
 * Rumpf von `POST agent/v1/operations/:operationId/result` (Agent -> Master).
 *
 * **Für `site.apply` und `site.remove` ausschließlich zur Fehlerdiagnose.** Der
 * Ist-Zustand einer Site wird niemals hieraus abgeleitet, sondern immer aus dem
 * Heartbeat-Inventar. Diese Rückmeldung darf ausfallen, ohne dass das System
 * hängen bleibt — der Reconciler konvergiert auch ohne sie, und genau das wird
 * im E2E-Test bewiesen.
 *
 * **Für `site.backup` und `site.restore` ist sie der Nachweis.** Das ist keine
 * Aufweichung der Regel, sondern ihre Kehrseite: Die Regel gilt, weil es bei
 * einem konvergierenden Verb ein Inventar gibt, das die Wahrheit besser kennt.
 * Für eine Sicherung gibt es das nicht — am Node ist hinterher nichts anders
 * als vorher. Ohne `backup` in dieser Rückmeldung könnte der Master die Frage
 * "wann wurde diese Site zuletzt gesichert, und hat es geklappt?" nicht
 * beantworten, und genau die muss das Kundenportal später beantworten.
 */
export const operationResultRequestSchema = z.object({
  siteId: uuidSchema,
  /** `succeeded` oder `failed`; alles andere entscheidet der Master selbst. */
  status: z.enum([OPERATION_STATUS.succeeded, OPERATION_STATUS.failed]),
  startedAt: isoDateTimeSchema,
  finishedAt: isoDateTimeSchema,
  /** Hash der Spezifikation, die tatsächlich angewandt wurde. */
  specHash: sha256HexSchema.nullable(),
  steps: z.array(operationStepSchema).max(32).default([]),
  error: operationErrorSchema.nullable().default(null),
  /**
   * Nur bei `site.backup` und `site.restore` befüllt.
   *
   * `.optional()` und nicht `.nullable().default(null)`: Ein Agent, der die
   * beiden Verben noch nicht kennt, schickt das Feld nicht — und soll deshalb
   * auch keinen Wert dafür erfinden müssen.
   */
  backup: backupResultSchema.optional(),
});

export type OperationResultRequest = z.infer<typeof operationResultRequestSchema>;

export const operationResultResponseSchema = z.object({
  /** `false`, wenn die Operation bereits abgeschlossen war — die Meldung kam zu spät. */
  accepted: z.boolean(),
  receivedAt: isoDateTimeSchema,
});

export type OperationResultResponse = z.infer<typeof operationResultResponseSchema>;
